import { LIVE_EXTEND_MAX_MS } from "./blocks";
import { type View, viewEnd, viewSpanMs, viewStart } from "./view";

/**
 * The window to actually request from the API.
 *
 * Two things happen here. It is widened beyond the viewport so a pan has data ready before
 * it needs it, and it is snapped to a grid so that panning a few pixels reuses the cached
 * response instead of issuing a request per frame. The grid step scales with the zoom, so
 * the number of distinct windows stays small at every scale rather than exploding when
 * zoomed in.
 */
export interface FetchWindow {
  startMs: number;
  endMs: number;
  width: number;
}

/** Whether two windows name the same request, so a pan that lands inside one keeps its data. */
export function sameWindow(a: FetchWindow | null, b: FetchWindow | null): boolean {
  if (a === null || b === null) return a === b;
  return a.startMs === b.startMs && a.endMs === b.endMs && a.width === b.width;
}

export const FETCH_OVERSCAN = 1.0; // one viewport of margin either side

export function fetchWindow(view: View): FetchWindow {
  const span = viewSpanMs(view);
  const quantum = Math.max(1000, span / 4);
  const center = Math.round(view.centerMs / quantum) * quantum;
  const half = span * (0.5 + FETCH_OVERSCAN);
  return {
    // Back past the live extension, not merely past the viewport. A running row's stored end
    // trails the clock by up to one worker pass, and `liveEndMs` draws it forward to the
    // present -- but only for a row that arrived, and the row is fetched on its stored span.
    // Zoom in far enough that the overscan reaches back less than that lag and the request
    // stops overlapping the row at all: the server returns nothing, and the block on air
    // disappears off the screen it fills. The reach is exactly `LIVE_EXTEND_MAX_MS`, since
    // that is as far forward as anything is ever drawn.
    startMs: center - half - LIVE_EXTEND_MAX_MS,
    endMs: center + half,
    // The server buckets tracks by the pixel width it is told about, so the width has to
    // describe the window being fetched, not the viewport inside it. Passing the viewport
    // width would make it bucket three times too coarsely. The live reach is left out of the
    // count deliberately: three minutes is nothing against a window wide enough to bucket at
    // all, and paying for it in pixels would push the width past the server's bound at the
    // tightest zooms.
    width: Math.round(view.width * (1 + 2 * FETCH_OVERSCAN)),
  };
}

/**
 * How much margin the envelope keeps even at its widest, as a fraction of the viewport.
 *
 * The centre is snapped to a quarter-viewport grid so that panning reuses a cached response,
 * and that snap can move the window by half a quantum -- an eighth of a viewport. Margin
 * thinner than the snap would leave an unpainted sliver at one edge.
 */
export const MIN_WAVEFORM_OVERSCAN = 1 / 8;

/**
 * The window to ask the loudness envelope for, or null when there is nothing honest to draw.
 *
 * Not :func:`fetchWindow`. That one always takes a viewport of margin either side, which is
 * free for the timeline but not here: the envelope route bounds itself by the bytes it reads
 * off disk, so three viewports spend the whole allowance on margin and the envelope switches
 * itself off from about a day of visible span outward -- the exact zoom it is wanted at, and
 * where a stale response left behind reads as a wrong one.
 *
 * So the margin is whatever is left under ``maxSpanMs`` once the viewport has its share: the
 * full overscan while zoomed in, tapering to none as the viewport approaches the bound. Same
 * snapped centre as the timeline's, so panning still reuses responses. No live reach either:
 * peaks are columns indexed by time, not rows with an end that lags, so there is nothing here
 * for a stale boundary to hide.
 */
export function waveformWindow(view: View, maxSpanMs: number): FetchWindow | null {
  const span = viewSpanMs(view);
  const overscan = Math.min(FETCH_OVERSCAN, (maxSpanMs / span - 1) / 2);
  // Wider than the route will answer with margin enough to cover the snap. Better to draw
  // nothing than a band that stops partway across the screen.
  if (!(overscan >= MIN_WAVEFORM_OVERSCAN)) return null;
  const quantum = Math.max(1000, span / 4);
  const center = Math.round(view.centerMs / quantum) * quantum;
  const half = span * (0.5 + overscan);
  return {
    startMs: center - half,
    endMs: center + half,
    width: Math.round(view.width * (1 + 2 * overscan)),
  };
}

/**
 * How far behind the live edge the envelope may fall, in columns.
 *
 * The window above asks for one column per viewport pixel, so a column is a pixel and this is
 * a distance on screen rather than a duration -- the only bound that means the same thing at
 * both ends of a zoom range four decimal orders wide.
 */
export const WAVEFORM_LAG_PX = 16;

/** However tight the zoom, the sidecars are not re-read faster than this. */
const WAVEFORM_MIN_REFRESH_MS = 2_000;

/**
 * How often to re-read the envelope of a window that reaches into the present.
 *
 * One interval cannot serve the whole zoom range. Thirty seconds of new audio is a few
 * thousandths of a pixel across a three-day window and a third of the screen across a minute
 * of it, so a fixed poll is either waste or a visible hole -- a hole of a different size at
 * every zoom, each holding its own cached answer, which is what left the envelope stopping
 * short of the playhead and jumping whenever the zoom changed.
 *
 * Bounded in pixels instead, so the gap reads the same at every scale, and floored so the
 * deepest zoom does not poll flat out for a lane that is decoration. At wide zooms this is
 * `ceilingMs` again, which is where it was never wrong.
 */
export function waveformRefreshMs(window_: FetchWindow | null, ceilingMs: number): number {
  // No window, or none measured yet: nothing is drawn, so the cadence is moot.
  if (window_ === null || window_.width <= 0) return ceilingMs;
  const msPerPx = (window_.endMs - window_.startMs) / window_.width;
  return Math.min(ceilingMs, Math.max(WAVEFORM_MIN_REFRESH_MS, msPerPx * WAVEFORM_LAG_PX));
}

// A ladder of round intervals. Anything not on it (7 minutes, 40 minutes) produces
// gridlines a person cannot count in their head.
const STEPS_MS = [
  1_000,
  2_000,
  5_000,
  10_000,
  15_000,
  30_000,
  60_000,
  2 * 60_000,
  5 * 60_000,
  10 * 60_000,
  15 * 60_000,
  30 * 60_000,
  3_600_000,
  2 * 3_600_000,
  3 * 3_600_000,
  6 * 3_600_000,
  12 * 3_600_000,
  86_400_000,
  2 * 86_400_000,
  7 * 86_400_000,
];

/** The finest ladder step that still leaves at least `minPx` between gridlines. */
export function gridStepMs(msPerPx: number, minPx = 90): number {
  const wanted = msPerPx * minPx;
  for (const step of STEPS_MS) {
    if (step >= wanted) return step;
  }
  return STEPS_MS[STEPS_MS.length - 1]!;
}

/**
 * Gridline instants across the viewport.
 *
 * Aligned to the epoch rather than to the window edge, so a line sits on the hour instead
 * of at some arbitrary offset that shifts as you pan. Below a day this is exact; at and
 * above a day the alignment is only exact in UTC, which is a deliberate simplification --
 * the labelled ticks are the ones that carry meaning and they are formatted in the
 * viewer's zone regardless.
 */
export function gridLines(view: View, stepMs: number): number[] {
  const start = viewStart(view);
  const end = viewEnd(view);
  const first = Math.ceil(start / stepMs) * stepMs;
  const out: number[] = [];
  for (let t = first; t <= end; t += stepMs) out.push(t);
  return out;
}
